'use client';

import React from 'react';
import _ from 'lodash';
import { Provider as MobxProvider } from 'mobx-react';
import { applySnapshot } from 'mobx-state-tree';

import { useAdmissionStore, useApplicationStore, useAppStore } from '@/stores';
import { useMentorCalendarStore } from '@/stores/MentorCalendarStore';
import { useStudentCalendarStore } from '@/stores/StudentCalendarStore';
import { useUserStore } from '@/stores/UserStore';
import type {
  AdmissionStoreSnapshotIn,
  ApplicationStoreSnapshotIn,
} from '@/stores';
import type { MentorCalendarStoreSnapshotIn } from '@/stores/MentorCalendarStore';
import type { StudentCalendarStoreSnapshotIn } from '@/stores/StudentCalendarStore';
import type { UserStoreSnapshotIn } from '@/stores/UserStore';

type StoreDefinition = {
  name: string;
  hook: Function;
};

const STORES = [
  { name: 'appStore', hook: useAppStore },
  { name: 'applicationStore', hook: useApplicationStore },
  { name: 'admissionStore', hook: useAdmissionStore },
  { name: 'userStore', hook: useUserStore },
  { name: 'mentorCalendarStore', hook: useMentorCalendarStore },
  { name: 'studentCalendarStore', hook: useStudentCalendarStore },
] as StoreDefinition[];

type Props = {
  applicationStoreSnapshot?: ApplicationStoreSnapshotIn;
  admissionStoreSnapshot?: AdmissionStoreSnapshotIn;
  userStoreSnapshot?: UserStoreSnapshotIn;
  mentorCalendarStoreSnapshot?: MentorCalendarStoreSnapshotIn;
  studentCalendarStoreSnapshot?: StudentCalendarStoreSnapshotIn;
  children: React.ReactNode;
};

const StoresProvider: React.FC<Props> = ({ children, ...snapshotProps }) => {
  const stores = STORES.reduce((acc, x) => {
    const snapshot = snapshotProps[`${x.name}Snapshot`];
    const store = x.hook();

    if (!_.isEmpty(snapshot)) {
      applySnapshot(store, snapshot);
    }

    acc[x.name] = store;
    return acc;
  }, {});

  return <MobxProvider {...stores}>{children}</MobxProvider>;
};

export default StoresProvider;
